import { LinkedCredential } from '../../models';
import { ApiClient, Ctor, DraftDescriptor, ReadDraftClaims } from '../../types';
import { draftMetadataToApiDraftMeta } from '../../utils/converters';
import { generateKey } from '../../utils/idempotency';
import { UdtClaims } from '../claims';
import { CredentialFlavor } from '../flavor';
import { Claims, DraftDataDescriptor, UpdatePayloadWithClaims } from '../types';
import { VerifiableCredentialWithClaims } from '../verifiable/with-claims';
import { DraftCredential } from './draft';

export class DraftCredentialWithClaims<T extends Claims> extends DraftCredential {
    private claims = new UdtClaims(this.client, this.claimsModel);

    constructor(
        client: ApiClient,
        private flavor: CredentialFlavor,
        private claimsModel: Ctor<T>,
        draftData: DraftDataDescriptor,
        descriptor: DraftDescriptor,
    ) {
        super(client, draftData, descriptor);
    }

    toLinkedCredential(): LinkedCredential<T> {
        return super.toLinkedCredential();
    }

    async getClaims(): Promise<ReadDraftClaims<T>> {
        const claims = this.draftData.values || {};

        return this.claims.unmarshalValuesToDraft(claims);
    }

    async update(request: UpdatePayloadWithClaims<T>): Promise<DraftCredentialWithClaims<T>> {
        let { etag, revision } = this.descriptor;
        let { draftData } = this;

        if (request.claims || request.metadata) {
            const updatedDraft = await this.client.drafts.draftUpdate(this.descriptor.id, {
                ifMatch: etag,
                idempotencyKey: generateKey('update'),
                data: {
                    values: request.claims ? this.claims.marshalValues(request.claims) : undefined,
                    meta: draftMetadataToApiDraftMeta(request.metadata),
                },
            });

            etag = updatedDraft.etag;
            revision = updatedDraft.revision;
            draftData = updatedDraft.data;
        }

        if (request.labels) {
            const updatedDraft = await this.client.drafts.draftUpdateLabels(this.descriptor.id, {
                ifMatch: etag,
                idempotencyKey: generateKey('update'),
                labels: request.labels,
            });

            etag = updatedDraft.etag;
            revision = updatedDraft.revision;
        }

        if (request.annotations) {
            const updatedDraft = await this.client.drafts.draftUpdateAnnotations(this.descriptor.id, {
                ifMatch: etag,
                idempotencyKey: generateKey('update'),
                annotations: request.annotations,
            });

            etag = updatedDraft.etag;
            revision = updatedDraft.revision;
        }

        return new DraftCredentialWithClaims(this.client, this.flavor, this.claimsModel, draftData, {
            id: this.descriptor.id,
            etag,
            revision,
        });
    }

    async issue(privateKeyId: string): Promise<VerifiableCredentialWithClaims<T>> {
        const { descriptor } = await super.issue(privateKeyId);

        return new VerifiableCredentialWithClaims(this.client, this.flavor, this.claimsModel, descriptor);
    }
}
